/**
 * Draggable participant chip with a remove (and optional edit) control.
 */

import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Button } from '@/components/forms/Button';
import { ParticipantChip } from './ParticipantChip';
import { X, Pencil, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserWithProfile } from '@/types/user.types';
import { getVolunteerRoleLabel } from '@/types/user.types';
import type { SlotAvailabilityStatus } from '@/types/slot-availability.types';
import type { AttendanceStatus } from '@/types/attendance.types';
import { ATTENDANCE_STATUS_LABELS } from '@/constants/shared-catalog';

/** Props for {@link RemovableParticipantChip}. */
interface RemovableParticipantChipProps {
  user: UserWithProfile;
  role: 'tutor' | 'student' | 'volunteer';
  onRemove: () => void;
  /** When set, shows a pencil button to edit the participant. */
  onEdit?: () => void;
  size?: 'sm' | 'md';
  className?: string;
  draggable?: boolean;
  slotAvailability?: SlotAvailabilityStatus;
  slotConflictHint?: string;
  /** Hide the role badge when it is already shown as a section title. */
  hideRoleLabel?: boolean;
  /** Same visual as session details {@link ParticipantChip} attendance tag */
  attendanceStatus?: AttendanceStatus;
  absenceJustification?: string | null;
}

/** Icon + colors for the attendance status tag on the chip. */
const ATTENDANCE_ICON_CONFIG = {
  present: {
    icon: CheckCircle,
    color: 'rgb(10, 134, 66)',
    bgColor: 'color-mix(in srgb, rgb(10, 134, 66) 15%, transparent)',
  },
  absent: {
    icon: XCircle,
    color: 'rgb(var(--color-error))',
    bgColor: 'color-mix(in srgb, rgb(var(--color-error)) 15%, transparent)',
  },
  excused: {
    icon: AlertCircle,
    color: 'rgb(var(--color-warning))',
    bgColor: 'color-mix(in srgb, rgb(var(--color-warning)) 15%, transparent)',
  },
  pending: {
    icon: Clock,
    color: 'var(--muted-foreground)',
    bgColor: 'color-mix(in srgb, var(--muted-foreground) 15%, transparent)',
  },
} as const;

/** Participant chip with a remove action. */
export const RemovableParticipantChip: React.FC<RemovableParticipantChipProps> = ({
  user,
  role,
  onRemove,
  onEdit,
  size = 'sm',
  className,
  draggable = false,
  slotAvailability,
  slotConflictHint,
  hideRoleLabel = false,
  attendanceStatus,
  absenceJustification,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `participant-${user.id}`,
    data: { type: role, user },
    disabled: !draggable,
  });

  const attendanceConfig = attendanceStatus ? ATTENDANCE_ICON_CONFIG[attendanceStatus] : null;
  const AttendanceIcon = attendanceConfig?.icon;

  return (
    <div className={cn('inline-flex items-center gap-1', isDragging && 'opacity-50', className)}>
      <div
        ref={draggable ? setNodeRef : undefined}
        {...(draggable ? listeners : {})}
        {...(draggable ? attributes : {})}
        className="relative inline-flex"
      >
        <ParticipantChip
          user={user}
          role={role}
          size={size}
          showTooltip={!!attendanceStatus}
          attendanceStatus={attendanceStatus}
          absenceJustification={absenceJustification}
          showAttendanceBadge={false}
          slotAvailability={slotAvailability}
          slotConflictHint={slotConflictHint}
          volunteerRoleLabel={
            !hideRoleLabel && role === 'volunteer'
              ? (getVolunteerRoleLabel(user) ?? undefined)
              : undefined
          }
          className={cn('!pr-14', draggable && '!cursor-grab active:!cursor-grabbing')}
        />
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 right-7 w-4 h-4 rounded-full border flex items-center justify-center',
              'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
              'text-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)]',
              'transition-all duration-200 hover:scale-110 hover:bg-[var(--color-2-nuance-1)] hover:text-white',
              'shadow-sm',
            )}
            aria-label={`Modifier ${user.firstName} ${user.lastName}`}
          >
            <Pencil className="w-2.5 h-2.5" />
          </button>
        )}
        <Button
          variant="ghost"
          size="icon"
          icon={X}
          iconOnly
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 right-1.5 w-4 h-4 rounded-full border',
            '!bg-[rgb(var(--color-error))] !text-white !border-[rgb(var(--color-error))]',
            'transition-all duration-200',
            'hover:scale-110 hover:brightness-90',
            'focus:ring-2 focus:ring-[rgb(var(--color-error)_/_0.5)]',
            'shadow-md',
          )}
          aria-label={`Retirer ${user.firstName} ${user.lastName}`}
        />
      </div>
      {attendanceConfig && AttendanceIcon && (
        <div
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border flex-shrink-0"
          style={{
            backgroundColor: attendanceConfig.bgColor,
            borderColor: attendanceConfig.color,
          }}
          title={ATTENDANCE_STATUS_LABELS[attendanceStatus!]}
          aria-label={ATTENDANCE_STATUS_LABELS[attendanceStatus!]}
        >
          <AttendanceIcon className="w-3.5 h-3.5" style={{ color: attendanceConfig.color }} />
        </div>
      )}
    </div>
  );
};
